import type { TableActionResponse } from '@matix/api-client';
import { useCart } from '../../store/cart';
import { useHeldOrders } from '../../store/heldOrders';
import { toast } from '../../store/toast';
import { trNow } from '../../lib/language';
import { openHeldOrder } from './openHeld';

/**
 * Shared by the table picker's Change / Move / Merge and the Split / Transfer
 * dialog. Every one of them acts on the held order AS SAVED on the server and
 * then reloads it into the cart, so an unsaved edit in the cart would be lost.
 */

/**
 * Has the cashier changed the reopened held order in ways the server does not
 * know yet? New lines, changed quantities, or an approved removal. (Customer,
 * discount and note edits are not tracked per line and are not caught here.)
 */
export function heldHasUnsavedChanges(): boolean {
  const s = useCart.getState();
  if (!s.editingHeld) return false;
  if (s.heldManagerPassword) return true;
  return s.lines.some(
    (l) => !l.isReturn && (l.heldBaseQty == null || Math.abs(l.qty - l.heldBaseQty) > 1e-9),
  );
}

/** Refuse (with a toast) while the cart holds unsaved changes. True = go ahead. */
export function requireSavedHeld(): boolean {
  if (!heldHasUnsavedChanges()) return true;
  toast(
    trNow({
      en: 'Hold the order first — it has changes that are not saved yet.',
      ar: 'علّق الطلب أولاً — به تغييرات لم تُحفظ بعد.',
    }),
    'warning',
  );
  return false;
}

/**
 * After an action: bring the order back into the cart as it now is, or clear
 * the cart when every item left it. The held-orders list refreshes either way.
 */
export async function reloadAfterTableAction(res: TableActionResponse): Promise<void> {
  void useHeldOrders.getState().refresh();
  if (res.still_open) {
    await openHeldOrder(res.order_id, { pay: false, tr: trNow });
  } else {
    useCart.getState().reset();
  }
}

/** The table-action refusals, in the session's language. Null = keep the server's text. */
export function tableActionErrorText(err: unknown): string | null {
  const data = (err as { response?: { data?: { code?: string; details?: { available?: number; permission?: string } } } })
    ?.response?.data;
  switch (data?.code) {
    case 'PERMISSION_DENIED':
      return trNow({
        en: `You do not have the "${data.details?.permission ?? ''}" permission.`,
        ar: `ليست لديك صلاحية "${data.details?.permission ?? ''}".`,
      });
    case 'TABLE_OCCUPIED':
      return trNow({
        en: 'That table is taken. Use Move to join the two orders, or pick a free table.',
        ar: 'هذه الطاولة مشغولة. استخدم نقل لدمج الطلبين، أو اختر طاولة متاحة.',
      });
    case 'TABLE_NOT_FOUND':
      return trNow({ en: 'That table does not belong to this branch.', ar: 'هذه الطاولة لا تتبع هذا الفرع.' });
    case 'SAME_TABLE':
      return trNow({
        en: 'The items are already on that table. Use Separate to split the bill.',
        ar: 'الأصناف موجودة على هذه الطاولة بالفعل. استخدم الفصل لتقسيم الفاتورة.',
      });
    case 'NOTHING_LEFT':
      return trNow({
        en: 'Leave at least one item on this order.',
        ar: 'اترك صنفاً واحداً على الأقل في هذا الطلب.',
      });
    case 'QTY_TOO_HIGH':
      return trNow({
        en: `Only ${data.details?.available ?? ''} of that item is on the order.`,
        ar: `الكمية المتاحة من هذا الصنف ${data.details?.available ?? ''} فقط.`,
      });
    case 'LINE_NOT_FOUND':
    case 'TARGET_CHANGED':
    case 'HELD_ORDER_NOT_FOUND':
      return trNow({
        en: 'The order changed on another till a moment ago. Reopen it and try again.',
        ar: 'تغيّر الطلب على جهاز آخر قبل لحظات. أعد فتحه وحاول مرة أخرى.',
      });
    default:
      return null;
  }
}
